/**
 * @file BillNotesDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-05
 * @description A bill's notes, editable on any bill — approved, paid or keyed
 *              in Xero (PATCH /api/bills/:id/notes). Notes are this system's
 *              own: they never go to Xero, and the sync keeps them. Audited.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import './BillLocalEdits.css';

const NOTES_MAX = 4000;

function NotesBody({ notes, setNotes, error, saving }) {
  useSlideOutButton([saving]);
  return (
    <div className="bill-local-body">
      <p className="bill-local-hint">Kept in this system only — notes are never sent to Xero.</p>
      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
        rows={6}
        maxLength={NOTES_MAX}
        disabled={saving}
        placeholder="Anything worth knowing about this bill"
        autoFocus
      />
      {error && <div className="bill-local-error" role="alert">{error}</div>}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.bill — id, notes, supplier_reference, bill_number
 * @param {function} props.onClose
 * @param {function} props.onSaved — (notes) after they were saved
 */
function BillNotesDialog({ bill, onClose, onSaved }) {
  const [notes, setNotes] = useState(bill?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  if (!bill) return null;
  const unchanged = (notes || '').trim() === (bill.notes || '').trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bills/${bill.id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: notes.trim() ? notes : null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || 'The notes could not be saved.');
        return;
      }
      onSaved && onSaved(data.notes ?? null);
      onClose && onClose();
    } catch (err) {
      setError(`The notes could not be saved: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const ref = bill.supplier_reference || bill.bill_number || `#${bill.id}`;
  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      title={`Notes — bill ${ref}`}
      size="md"
      className="bill-local"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" data-write-action onClick={save} disabled={saving || unchanged}
                    title={unchanged ? 'Nothing has changed' : 'Save the notes'}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>{saving ? 'progress_activity' : 'save'}</span>
              </div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      }
    >
      <NotesBody notes={notes} setNotes={setNotes} error={error} saving={saving} />
    </Modal>
  );
}

export default BillNotesDialog;
